/**
 * @fileOverview Defines a pagination component for paging through record sets,
 * shows a summary of which records you are viewing together with the total
 * amount of records. Provides a left and right paging arrow and an input to
 * quickly navigate to a specific page.
 */

import React, { Fragment } from 'react'
import injectStyles from 'react-jss'
import AnimatedArrow from 'Components/Icons/AnimatedArrow'
import classNames from 'classnames'
import { numberFormatter } from './../../Utils/Formatters'

const styles = {
  container: {
    marginTop: 15,
    marginBottom: 15,
  },
  arrow: {
    width: 24,
    marginLeft: 24,
    cursor: 'pointer',
  },
  input: {
    border: '1px solid #dee2e5',
    backgroundColor: 'white',
    padding: '5px 6px',
  },
}

/**
 * @typedef PaginationProps
 * @property {number} total The total amount of records
 * @property {number} offset The offset (or amount of records skipped) before
 * indicating which records are shown
 * @property {number} resultsPerPage The amount of records to show per page
 * @property {Function} onOffsetChange When the offset is changed this method
 * will be triggered, it's only argument will be the new offset value.
 *
 * @param {PaginationProps} props
 */

/**
 * On hitting enter, submit the current value int he page number input
 */

const onKeyDown = (e, func) => {
  if (e.keyCode == 13) {
    e.preventDefault()
    return func(e)
  }
}
const Pagination = props => {
  const {
    classes,
    className,
    total,
    offset,
    resultsPerPage,
    onOffsetChange,
    hasEditableAmountItemsPerPage,
  } = props

  if (total === undefined || total === null)
    throw new Error("Missing prop 'total'.")

  if (offset === undefined || offset === null)
    throw new Error("Missing prop 'offset'.")

  if (resultsPerPage === undefined || resultsPerPage === null)
    throw new Error("Missing prop 'resultsPerPage'.")

  /**
   * Fired when the page number input is changed.
   */
  const onChangePage = e => {
    var page = Number(e.target.innerText)
    if (isNaN(page)) return
    var newOffset = page - 1
    if (newOffset === offset) return
    props.onOffsetChange(newOffset * resultsPerPage)
  }

  const onChangeItemsPerPage = e => {
    const items = Number(e.target.innerText)
    if (isNaN(items)) return
    props.setResultsPerPage(items)
  }

  /**
   * When the page number input is selected, automatically highlight the content
   * to make it a little easier to use.
   */
  const onPageNumberInputFocus = e => {
    setTimeout(() => document.execCommand('selectAll', false, null), 4)
  }

  var first = offset + 1,
    last = Math.min(offset + resultsPerPage, total)

  var totalPages = Math.ceil(total / resultsPerPage),
    page = Math.round(offset / resultsPerPage) + 1

  // When there's no records we show a different summary
  if (total === 0) {
    return (
      <div className={classNames(classes.container, className)}>
        showing 0 records
      </div>
    )
  }

  return (
    <div className={classNames(classes.container, className, 'Pagination')}>
      Page{' '}
      <span
        className={classes.input}
        contenteditable="true"
        onFocus={onPageNumberInputFocus}
        onKeyDown={e => onKeyDown(e, onChangePage)}
        onBlur={onChangePage}
      >
        {page}
      </span>{' '}
      of {totalPages}, showing {numberFormatter(first)}-{numberFormatter(last)}{' '}
      of {numberFormatter(total)} records
      <AnimatedArrow
        orientation="left"
        height={8}
        className={classes.arrow}
        fill="#757575"
        onClick={() => onOffsetChange(offset - resultsPerPage)}
      />
      <AnimatedArrow
        orientation="right"
        height={8}
        className={classes.arrow}
        fill="#757575"
        onClick={() => onOffsetChange(offset + resultsPerPage)}
      />
      {hasEditableAmountItemsPerPage ? (
        <Fragment>
          {' '}
          Items per page{' '}
          <span
            className={classes.input}
            contenteditable="true"
            onFocus={onPageNumberInputFocus}
            onKeyDown={e => onKeyDown(e, onChangeItemsPerPage)}
            onBlur={onChangeItemsPerPage}
          >
            {resultsPerPage}
          </span>{' '}
        </Fragment>
      ) : null}
    </div>
  )
}

const StyledPagination = injectStyles(styles)(Pagination)

export default StyledPagination



// WEBPACK FOOTER //
// ./src/Components/FilterAndPaginationComponent/Pagination.js